import React, { useEffect, useMemo, useState } from "react";
import {
    ArrowRightIcon,
    ChevronLeftIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { ReactComponent as UserBoldIcon } from "./icons/user-bold.svg";
import { useSystemLogo } from "../../context/systemConfigContext";

export default function SeleccionarDoctorPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logo } = useSystemLogo();
    const [query, setQuery] = useState("");

    const [doctorsRaw, setDoctorsRaw] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const patientName = location.state?.patientName ?? "Juan Pérez";
    const patient = location.state?.patient ?? null;
    const govId = location.state?.govId ?? null;

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            try {
                setErrorMessage("");
                setLoading(true);
                const data = await api.users.getPublicDoctors(null, null);
                if (!mounted) return;
                setDoctorsRaw(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!mounted) return;
                setDoctorsRaw([]);
                setErrorMessage(
                    typeof e?.message === "string"
                        ? e.message
                        : "No pudimos cargar los doctores. Intentá nuevamente."
                );
            } finally {
                if (mounted) setLoading(false);
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, []);

    const normalizeDoctor = (u) => {
        const firstName = u?.firstName ?? u?.first_name ?? "";
        const lastName = u?.lastName ?? u?.last_name ?? "";
        const fullName = `${firstName} ${lastName}`.trim();
        const specialtyLabel =
            u?.specialtyName ??
            u?.specialty_name ??
            u?.specialty?.name ??
            (typeof u?.specialty === "string" ? u.specialty : "");

        return {
            id: u?.id,
            name: fullName.length > 0 ? `Dr. ${fullName}` : "Doctor",
            specialty: specialtyLabel || "Especialidad",
            raw: u,
        };
    };

    const doctors = useMemo(() => {
        const q = query.trim().toLowerCase();
        return doctorsRaw
            .map(normalizeDoctor)
            .filter((d) => {
                if (!q) return true;
                return (
                    d.name.toLowerCase().includes(q) ||
                    d.specialty.toLowerCase().includes(q)
                );
            });
    }, [doctorsRaw, query]);

    return (
        <div className="min-h-screen bg-[#F1F1FE] px-4 py-6 font-poppins md:flex md:items-center md:justify-center">
            <div className="w-full max-w-sm mx-auto md:max-w-[640px]">
                <div className="hidden md:block">
                    <div className="relative flex items-center justify-center">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="absolute left-0 flex items-center gap-2 text-[#000000] font-medium"
                        >
                            <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                                <ChevronLeftIcon className="h-4 w-4 text-white" />
                            </div>
                            <span>Atrás</span>
                        </button>
                        <img className="h-auto w-40" src={logo} alt="Logo" />
                    </div>
                </div>

                <div className="md:hidden">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#000000] font-medium"
                    >
                        <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                            <ChevronLeftIcon className="h-4 w-4 text-white" />
                        </div>
                        <span>Atrás</span>
                    </button>
                </div>

                <div className="mt-6 bg-white rounded-2xl shadow-sm px-5 py-6">
                    <div className="bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-3 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#EFEFFF] flex items-center justify-center">
                            <UserBoldIcon className="h-5 w-5" />
                        </div>
                        <div className="text-xl font-semibold tracking-[0.5px] text-primary">
                            Hola, {patientName}
                        </div>
                    </div>

                    <h1 className="mt-6 text-center text-xl font-medium tracking-[0.5px] text-[#111827]">
                        Selecciona tu Doctor
                    </h1>

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-[#344054]">
                            Buscá por nombre o especialidad
                        </label>
                        <div className="relative mt-2">
                            <MagnifyingGlassIcon className="h-5 w-5 text-primary absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Buscar doctor o especialidad"
                                className="input-base pl-10 pr-3 py-3 text-sm"
                            />
                        </div>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-3">
                        {errorMessage ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
                                {errorMessage}
                            </div>
                        ) : null}

                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="w-6 h-6 border-b-2 border-primary rounded-full animate-spin" />
                            </div>
                        ) : null}

                        {doctors.map((d) => (
                            <button
                                key={d.id ?? `${d.name}-${d.specialty}`}
                                type="button"
                                onClick={() =>
                                    navigate("/reserve/select-slot", {
                                        state: {
                                            doctor: d,
                                            patientName,
                                            patient,
                                            govId,
                                        },
                                    })
                                }
                                className="w-full bg-[#F7F7FF] border border-[#ECECFF] rounded-xl px-4 py-3 flex items-center gap-3 text-left"
                            >
                                <div className="h-10 w-10 rounded-full bg-[#EFEFFF] flex items-center justify-center flex-shrink-0">
                                    <UserBoldIcon className="h-5 w-5" />
                                </div>
                                <div className="flex-1 tracking-[0.5px]">
                                    <div className="text-sm font-medium text-[#111827]">
                                        {d.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {d.specialty}
                                    </div>
                                </div>
                                <ArrowRightIcon className="h-5 w-5 text-[#111827]" />
                            </button>
                        ))}

                        {!loading && doctors.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 py-6">
                                No se encontraron resultados.
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
